import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { 
  Save, RefreshCw, Eye, EyeOff, Image, Video, ExternalLink, 
  MousePointer, BarChart3, Calendar, Play
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const CMSPopupPage = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState({ total_clicks: 0, recent_clicks: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [configRes, statsRes] = await Promise.all([
        api.get('/cms/popup'),
        api.get('/cms/popup/stats')
      ]);
      setConfig(configRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/popup', config);
      toast.success('Configuration enregistrée');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Popup d'accueil</h1>
          <p className="text-slate-500">
            Gérez la popup qui s'affiche à l'ouverture du site (vœux, annonces, promotions...)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
            <Eye className="w-4 h-4 mr-2" /> Aperçu
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-600">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Stats Card */}
      <Card className="border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white rounded-full shadow">
                <MousePointer className="w-6 h-6 text-cyan-500" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Clics sur le CTA</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total_clicks}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={config?.enabled ? "default" : "secondary"} className={config?.enabled ? "bg-green-500" : ""}>
                {config?.enabled ? "Activée" : "Désactivée"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Configuration</CardTitle>
            <CardDescription>Paramètres de la popup</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Activer la popup</p>
                <p className="text-sm text-slate-500">La popup s'affichera à l'ouverture du site</p>
              </div>
              <Switch
                checked={config?.enabled || false}
                onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
              />
            </div>

            {/* Show Once */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Afficher une seule fois</p>
                <p className="text-sm text-slate-500">Ne pas réafficher si déjà fermée</p>
              </div>
              <Switch
                checked={config?.show_once !== false}
                onCheckedChange={(checked) => setConfig({ ...config, show_once: checked })}
              />
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début (optionnel)</Label>
                <Input
                  type="date"
                  value={config?.start_date || ''}
                  onChange={(e) => setConfig({ ...config, start_date: e.target.value || null })}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin (optionnel)</Label>
                <Input
                  type="date"
                  value={config?.end_date || ''}
                  onChange={(e) => setConfig({ ...config, end_date: e.target.value || null })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        <Card>
          <CardHeader>
            <CardTitle>Contenu</CardTitle>
            <CardDescription>Texte et médias de la popup</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Titre</Label>
              <Input
                value={config?.title || ''}
                onChange={(e) => setConfig({ ...config, title: e.target.value })}
                placeholder="Bonne Année 2026 !"
              />
            </div>

            <div className="space-y-2">
              <Label>Texte</Label>
              <Textarea
                value={config?.text || ''}
                onChange={(e) => setConfig({ ...config, text: e.target.value })}
                placeholder="Toute l'équipe vous souhaite..."
                rows={3}
              />
            </div>

            {/* Media Type */}
            <div className="space-y-2">
              <Label>Type de média</Label>
              <div className="flex gap-2">
                <Button
                  variant={config?.media_type === 'image' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setConfig({ ...config, media_type: 'image' })}
                >
                  <Image className="w-4 h-4 mr-2" /> Image
                </Button>
                <Button
                  variant={config?.media_type === 'youtube' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setConfig({ ...config, media_type: 'youtube' })}
                >
                  <Video className="w-4 h-4 mr-2" /> Vidéo YouTube
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{config?.media_type === 'youtube' ? 'URL YouTube' : 'URL de l\'image'}</Label>
              <Input
                value={config?.media_url || ''}
                onChange={(e) => setConfig({ ...config, media_url: e.target.value })}
                placeholder={config?.media_type === 'youtube' ? 'https://youtube.com/watch?v=...' : 'https://...'}
              />
            </div>

            {/* CTA */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Texte du bouton CTA</Label>
                <Input
                  value={config?.cta_text || ''}
                  onChange={(e) => setConfig({ ...config, cta_text: e.target.value })}
                  placeholder="Découvrir"
                />
              </div>
              <div className="space-y-2">
                <Label>Lien du CTA</Label>
                <Input
                  value={config?.cta_link || ''}
                  onChange={(e) => setConfig({ ...config, cta_link: e.target.value })}
                  placeholder="/site#services"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview */}
      {showPreview && (
        <Card>
          <CardHeader>
            <CardTitle>Aperçu</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border">
              {/* Media */}
              {config?.media_url && (
                <div className="aspect-video bg-slate-100">
                  {config.media_type === 'youtube' ? (
                    <iframe
                      src={getYouTubeEmbedUrl(config.media_url)}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <img 
                      src={config.media_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              )}
              
              {/* Content */}
              <div className="p-6 text-center">
                <h3 className="text-xl font-bold text-slate-900 mb-2">{config?.title || 'Titre'}</h3>
                <p className="text-slate-600 mb-4">{config?.text || 'Texte de la popup...'}</p>
                {config?.cta_text && (
                  <Button className="bg-gradient-to-r from-cyan-500 to-blue-600">
                    {config.cta_text}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Clicks */}
      {stats.recent_clicks?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Derniers clics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.recent_clicks.slice(0, 10).map((click, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                  <MousePointer className="w-4 h-4 text-cyan-500" />
                  <span className="text-sm text-slate-600">
                    {new Date(click.timestamp).toLocaleString('fr-FR')}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CMSPopupPage;
